import type { GridSortModel } from '@mui/x-data-grid-pro';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { tableContainer } from '@src/common/components/tableStyle/TableStyle';
import { theme } from '@src/common/styles/Theme';
import { useSearchPermits } from '@src/permits/hooks/useSearchPermits';
import { PermitFilters } from '@src/permits/permitSearch/PermitFilters';
import { PermitSearchButtons } from '@src/permits/permitSearch/PermitSearchButtons';
import { SearchResult } from '@src/permits/permitSearch/SearchResult';
import { SortingMode } from '@src/permits/permitSearch/SortingMode';
import { sortingModeAssembler } from '@src/permits/permitSearch/SortingModeAssembler';
import type {
  SearchPermitBasicFilters,
  SearchPermitFilters,
} from '@src/permits/services/PermitApplicationClient';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

const EMPTY_FILTERS: SearchPermitBasicFilters = {
  type: [],
  poNumber: '',
  client: [],
  jurisdiction: [],
  status: [],
  stateId: '',
  effectiveDate: undefined,
  submissionDate: undefined,
  tractorUnitNumber: '',
  tractorLicensePlateNumber: '',
  claimedBy: undefined,
};

export const PermitSearch = () => {
  const { t } = useTranslation();

  const [filters, setFilters] =
    useState<SearchPermitBasicFilters>(EMPTY_FILTERS);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const { searchPermits, permits, isLoading } = useSearchPermits();
  const updateFilters = (override: SearchPermitFilters) => {
    setFilters({ ...filters, ...override });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSortModel([]);
  };

  const handleSortModelChange = useCallback(
    (newGridSortModel: GridSortModel) => {
      setSortModel(newGridSortModel);

      const combinedFilters: SearchPermitFilters = { ...filters };
      const sortingMode = sortingModeAssembler(newGridSortModel);
      if (sortingMode === SortingMode.SERVER && newGridSortModel.length > 0) {
        combinedFilters.sortBy = {
          field: newGridSortModel[0].field || '',
          direction: newGridSortModel[0].sort || '',
        };
      }
      searchPermits(combinedFilters);
    },
    [filters, searchPermits],
  );

  return (
    <PageLayout title={t('permitSearch.title')}>
      <Stack className={tableContainer} flex="1">
        <Stack
          paddingX={theme.spacing['5']}
          gap={theme.spacing['3']}
          paddingBottom={!permits ? theme.spacing['4'] : '0'}
        >
          <PermitFilters filters={filters} onFilterChange={updateFilters} />

          <PermitSearchButtons
            searchPermits={searchPermits}
            filters={filters}
            resetFilters={resetFilters}
            permits={permits}
          />
        </Stack>

        {permits && (
          <SearchResult
            permits={permits}
            isLoading={isLoading}
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
          />
        )}
      </Stack>
    </PageLayout>
  );
};
